import { useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "./atoms";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "./atoms";
import { AddIcon, CloseIcon } from "./atoms/Icon";
import { ConfirmDeleteModal } from "./molecules/ConfirmDeleteModal";
import { Tag } from "./atoms/Tag/Tag";

/**
 * タグマスタ（従業員タグ / シフトパターンタグ 共通）
 */
export interface TagMaster {
    id: number;
    name: string;
    color: string;
}

/**
 * タグ作成・更新時のフォーム値
 */
export interface TagFormValues {
    name: string;
    color: string;
}

interface TagSelectorPanelProps {
    tags: TagMaster[];
    selectedIds: number[];
    onSelectedChange: (ids: number[]) => void;
    onCreate: (data: TagFormValues) => Promise<void>;
    onUpdate: (id: number, data: TagFormValues) => Promise<void>;
    onDelete: (id: number) => Promise<void>;
    loading?: boolean;
    errorMessage?: string | null;
}

/**
 * タグ色プリセット（HEX 値をそのまま保存）
 * Tag atom は枠線＋左ドットの白地デザインなので、視認性のため medium / vivid 系から 8 色を採用。
 */
const TAG_COLOR_PRESETS: { value: string; label: string }[] = [
    { value: "#ff8f8a", label: "シュリンプピンク" },
    { value: "#f39b4f", label: "ゴールデンイエロー" },
    { value: "#dfca77", label: "きはだ色" },
    { value: "#c0d37e", label: "シャトルーズグリーン" },
    { value: "#87dcb2", label: "ミントグリーン" },
    { value: "#78b2f9", label: "勿忘草色" },
    { value: "#ca9ee7", label: "ライラック" },
    { value: "#f491b6", label: "ローズピンク" },
];

/**
 * タグ選択パネル（従業員タグ・シフトパターンタグ共通）
 */
export const TagSelectorPanel = ({
    tags,
    selectedIds,
    onSelectedChange,
    onCreate,
    onUpdate,
    onDelete,
    loading = false,
    errorMessage,
}: TagSelectorPanelProps) => {
    const [dialogState, setDialogState] = useState<
        | { mode: "create" }
        | { mode: "edit"; tag: TagMaster }
        | null
    >(null);
    const [deleteTarget, setDeleteTarget] = useState<TagMaster | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [pendingTagId, setPendingTagId] = useState<string>("");

    const selectedTags = useMemo(
        () => tags.filter((t) => selectedIds.includes(t.id)),
        [tags, selectedIds],
    );
    const unselectedTags = useMemo(
        () => tags.filter((t) => !selectedIds.includes(t.id)),
        [tags, selectedIds],
    );

    const handleDetach = (tagId: number) => {
        onSelectedChange(selectedIds.filter((id) => id !== tagId));
    };

    const handleAttach = () => {
        const tagId = Number(pendingTagId);
        if (Number.isNaN(tagId) || selectedIds.includes(tagId)) return;
        onSelectedChange([...selectedIds, tagId]);
        setPendingTagId("");
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await onDelete(deleteTarget.id);
            setDeleteTarget(null);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="flex flex-col gap-8">
            {errorMessage && (
                <div className="text-sm text-red-600">{errorMessage}</div>
            )}

            {/* 紐付け済みのタグ */}
            <section>
                <div className="flex gap-8">
                    <label className="text-sm text-gray-700 w-32 flex-shrink-0 pt-2">
                        紐付け済みのタグ
                    </label>
                    <div className="flex-1 border border-gray-200 rounded-lg p-4 min-h-24">
                        {selectedTags.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                                {selectedTags.map((tag) => (
                                    <TagChip
                                        key={tag.id}
                                        tag={tag}
                                        onRemove={() => handleDetach(tag.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <span className="text-sm text-gray-400">
                                紐付け済みのタグはありません
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {/* タグを追加 */}
            <section>
                <div className="flex gap-8 items-start">
                    <label className="text-sm text-gray-700 w-32 flex-shrink-0 pt-2">
                        タグを追加
                    </label>
                    <div className="flex-1 flex items-center gap-3">
                        <div className="relative w-64">
                            <select
                                value={pendingTagId}
                                disabled={loading || unselectedTags.length === 0}
                                onChange={(e) => setPendingTagId(e.target.value)}
                                className={clsx(
                                    "w-full appearance-none rounded-lg border border-gray-300 bg-white pl-3 pr-10 py-2 text-sm",
                                    "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
                                    "disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60",
                                )}
                            >
                                <option value="" disabled>
                                    {unselectedTags.length === 0
                                        ? "追加できるタグがありません"
                                        : "タグを選択"}
                                </option>
                                {unselectedTags.map((tag) => (
                                    <option key={tag.id} value={String(tag.id)}>
                                        {tag.name}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                <CaretIcon />
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="md"
                            onClick={handleAttach}
                            disabled={loading || pendingTagId === ""}
                        >
                            追加
                        </Button>
                        <button
                            type="button"
                            onClick={() => setDialogState({ mode: "create" })}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            <AddIcon className="w-4 h-4" />
                            新規追加
                        </button>
                    </div>
                </div>
            </section>

            {/* タグ一覧 */}
            <section>
                <div className="flex gap-8">
                    <label className="text-sm text-gray-700 w-32 flex-shrink-0 pt-2">
                        タグ一覧
                    </label>
                    <div className="flex-1 border border-gray-200 rounded-lg p-4 min-h-24">
                        {tags.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                                {tags.map((tag) => (
                                    <TagManageChip
                                        key={tag.id}
                                        tag={tag}
                                        onClick={() =>
                                            setDialogState({ mode: "edit", tag })
                                        }
                                    />
                                ))}
                            </div>
                        ) : (
                            <span className="text-sm text-gray-400">
                                タグがまだありません。「新規追加」から作成してください。
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {dialogState && (
                <TagFormDialog
                    initial={dialogState.mode === "edit" ? dialogState.tag : null}
                    onClose={() => setDialogState(null)}
                    onSubmit={async (values) => {
                        if (dialogState.mode === "edit") {
                            await onUpdate(dialogState.tag.id, values);
                        } else {
                            await onCreate(values);
                        }
                        setDialogState(null);
                    }}
                    onRequestDelete={
                        dialogState.mode === "edit"
                            ? () => {
                                  const target = dialogState.tag;
                                  setDialogState(null);
                                  setDeleteTarget(target);
                              }
                            : undefined
                    }
                />
            )}

            <ConfirmDeleteModal
                isOpen={deleteTarget !== null}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
                message={
                    deleteTarget
                        ? `タグ「${deleteTarget.name}」を削除します。このタグを使用している紐づけも解除されます。よろしいですか？`
                        : undefined
                }
            />
        </div>
    );
};

/* ─────────── TagChip ─────────── */

interface TagChipProps {
    tag: TagMaster;
    onRemove?: () => void;
}

const TagChip = ({ tag, onRemove }: TagChipProps) => (
    <span className="inline-flex items-center gap-1">
        <Tag label={tag.name} color={tag.color} />
        {onRemove && (
            <button
                type="button"
                onClick={onRemove}
                aria-label={`${tag.name} を解除`}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
                <CloseIcon className="h-3 w-3" />
            </button>
        )}
    </span>
);

/* ─────────── TagManageChip（一覧の編集用チップ） ─────────── */

interface TagManageChipProps {
    tag: TagMaster;
    onClick: () => void;
}

const TagManageChip = ({ tag, onClick }: TagManageChipProps) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={`${tag.name} を編集`}
        className="inline-flex items-center cursor-pointer rounded-l-[14px] transition-all hover:ring-2 hover:ring-brand-500 hover:ring-offset-1"
    >
        <Tag label={tag.name} color={tag.color} />
    </button>
);

/* ─────────── TagFormDialog ─────────── */

interface TagFormDialogProps {
    initial: TagMaster | null;
    onClose: () => void;
    onSubmit: (values: TagFormValues) => Promise<void>;
    /** 編集時のみ指定: 削除ボタン押下時に呼ばれる */
    onRequestDelete?: () => void;
}

const TAG_NAME_MAX_LENGTH = 7;

const TagFormDialog = ({ initial, onClose, onSubmit, onRequestDelete }: TagFormDialogProps) => {
    const [name, setName] = useState(initial?.name ?? "");
    const [color, setColor] = useState(initial?.color ?? TAG_COLOR_PRESETS[0].value);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const trimmedName = name.trim();
    const isValid =
        trimmedName !== "" && trimmedName.length <= TAG_NAME_MAX_LENGTH;

    const handleSubmit = async () => {
        if (!isValid || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit({ name: trimmedName, color });
        } catch (e) {
            setError(e instanceof Error ? e.message : "保存に失敗しました");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog onClose={submitting ? undefined : onClose} className="w-[480px]">
            <DialogHeader
                title={initial ? "タグを編集" : "タグを新規追加"}
                onClose={submitting ? undefined : onClose}
            />
            <DialogBody>
                <div className="flex flex-col gap-4">
                    {error && <div className="text-sm text-red-600">{error}</div>}
                    <div className="flex items-start gap-4">
                        <label className="text-sm text-gray-700 w-20 pt-2">名称</label>
                        <div className="flex-1 flex flex-col gap-1">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="タグ名"
                                maxLength={TAG_NAME_MAX_LENGTH}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            />
                            <span className="text-xs text-gray-400 self-end">
                                {trimmedName.length} / {TAG_NAME_MAX_LENGTH} 文字
                            </span>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <label className="text-sm text-gray-700 w-20 pt-2">色</label>
                        <div className="flex-1 flex flex-wrap gap-2">
                            {TAG_COLOR_PRESETS.map((preset) => {
                                const selected = color === preset.value;
                                return (
                                    <button
                                        key={preset.value}
                                        type="button"
                                        onClick={() => setColor(preset.value)}
                                        aria-label={preset.label}
                                        aria-pressed={selected}
                                        title={preset.label}
                                        className={clsx(
                                            "inline-flex items-center rounded-l-[14px] transition-all",
                                            selected
                                                ? "ring-2 ring-offset-1 ring-gray-800"
                                                : "hover:ring-2 hover:ring-offset-1 hover:ring-gray-300",
                                        )}
                                    >
                                        <Tag
                                            label={trimmedName || preset.label}
                                            color={preset.value}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </DialogBody>
            <DialogFooter className="!justify-between">
                <div>
                    {initial && onRequestDelete && (
                        <Button
                            variant="danger"
                            size="md"
                            onClick={onRequestDelete}
                            disabled={submitting}
                        >
                            削除
                        </Button>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="md"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        キャンセル
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={!isValid || submitting}
                    >
                        {submitting ? "保存中..." : initial ? "更新する" : "追加する"}
                    </Button>
                </div>
            </DialogFooter>
        </Dialog>
    );
};

/* ─────────── CaretIcon (select 右端の下向き矢印) ─────────── */

const CaretIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className="w-4 h-4"
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
);

export default TagSelectorPanel;
